import type { Destination } from '@sentinel/shared';
import type { TFunction } from 'i18next';
import { formatDateTime, formatTime } from '../i18n/format';

const D = 'accounts.destinations';

/** Title of a destination card. */
export function destinationTitle(destination: Destination, t: TFunction): string {
  return destination.kind === 'console' ? t(`${D}.console`) : destination.name;
}

/** Second line: the roles held there, the platform role, or the support window. */
export function destinationDetail(destination: Destination, t: TFunction): string {
  if (destination.kind === 'console') return t(`${D}.platformRoles.${destination.platformRole}`);
  if (destination.kind === 'support')
    return t(`${D}.window`, { t: formatTime(destination.expiresAt) });
  return destination.roles.join(' · ');
}

/** When it was last opened from a sign-in; support windows are not remembered. */
export function destinationLastUse(destination: Destination, t: TFunction): string | null {
  if (destination.kind === 'support') return null;
  return destination.lastUsedAt === null
    ? t(`${D}.never`)
    : t(`${D}.last`, { t: formatDateTime(destination.lastUsedAt) });
}

/** Stable key of a destination, for lists and for the countdown target. */
export function destinationKey(destination: Destination): string {
  if (destination.kind === 'console') return 'console';
  if (destination.kind === 'support') return `support:${destination.grantId}`;
  return `organization:${destination.organizationId}`;
}

/** Why an organization cannot be entered with the method of this session, or null. */
export function unreachableReason(destination: Destination, t: TFunction): string | null {
  if (destination.kind !== 'organization' || destination.reachable) return null;
  return t(destination.googleSignIn === 'required' ? `${D}.needsGoogle` : `${D}.needsPassword`);
}
